"use server";

import { v4 as uuidv4 } from "uuid";

import { prisma } from "@/prisma";
import { auth } from "@/auth";
import { requireRole } from "@/lib/permissions";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import type { ActionResult } from "@/types/server-action";
import {
  addColorRuleSchema,
  deleteColorRuleSchema,
  editColorRuleSchema,
} from "@/lib/schemas/color-rules";

function isValidColorCode(code: string): boolean {
  return code.length === 4;
}

export async function addColorRule(formData: unknown): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = addColorRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const {
    brandLetter,
    year,
    exclusiveProgram,
    codeColor,
    labelColor,
    dominantNuanceCode,
    dominantColorCode,
  } = parsed.data;

  if (!isValidColorCode(codeColor)) {
    return {
      success: false,
      fieldErrors: {
        codeColor: ["Le code couleur doit contenir exactement 4 caractères."],
      },
    };
  }

  const existing = await prisma.colorRule.findFirst({
    where: {
      deletedAt: null,
      brandLetter,
      year,
      exclusiveProgram,
      dominantNuanceCode,
      dominantColorCode,
    },
  });

  if (existing) {
    return {
      success: false,
      message:
        "Conflit : une règle active existe déjà pour ce couple (marque, année, exclusif, dominante 1, dominante 2).",
    };
  }

  await prisma.colorRule.create({
    data: {
      brandLetter,
      year,
      exclusiveProgram,
      codeColor,
      labelColor,
      dominantNuanceCode,
      dominantColorCode,
      isActive: true,
      version: 1,
      versionGroupId: uuidv4(),
      versionNote: null,
      deletedAt: null,
    },
  });

  return { success: true };
}

export async function editColorRule(formData: unknown): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = editColorRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const {
    id,
    brandLetter,
    year,
    exclusiveProgram,
    codeColor,
    labelColor,
    dominantNuanceCode,
    dominantColorCode,
    versionNote,
    isActive,
  } = parsed.data;

  if (!isValidColorCode(codeColor)) {
    return {
      success: false,
      fieldErrors: {
        codeColor: ["Le code couleur doit contenir exactement 4 caractères."],
      },
    };
  }

  const existing = await prisma.colorRule.findFirst({
    where: { id, deletedAt: null },
  });

  if (!existing) {
    return { success: false, message: "Règle introuvable" };
  }

  const mappingChanged =
    existing.brandLetter !== brandLetter ||
    existing.year !== year ||
    existing.exclusiveProgram !== exclusiveProgram ||
    existing.dominantNuanceCode !== dominantNuanceCode ||
    existing.dominantColorCode !== dominantColorCode;

  if (mappingChanged) {
    const conflict = await prisma.colorRule.findFirst({
      where: {
        deletedAt: null,
        brandLetter,
        year,
        exclusiveProgram,
        dominantNuanceCode,
        dominantColorCode,
      },
    });

    if (conflict) {
      return {
        success: false,
        message:
          "Conflit : une règle active existe déjà pour le mapping cible (marque, année, exclusif, dominante 1, dominante 2).",
      };
    }
  }

  await prisma.$transaction([
    prisma.colorRule.update({
      where: { id },
      data: { deletedAt: new Date(), isActive: false },
    }),
    prisma.colorRule.create({
      data: {
        brandLetter,
        year,
        exclusiveProgram,
        codeColor,
        labelColor,
        dominantNuanceCode,
        dominantColorCode,
        isActive,
        version: existing.version + 1,
        versionGroupId: existing.versionGroupId,
        versionNote: versionNote ?? null,
        deletedAt: null,
      },
    }),
  ]);

  return { success: true };
}

export async function deleteColorRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = deleteColorRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const { id } = parsed.data;

  await prisma.colorRule.update({
    where: { id },
    data: { deletedAt: new Date(), isActive: false },
  });

  return { success: true };
}
